import { useEffect, useRef } from 'react'
import styles from './Confetti.module.css'
import { useMediaQuery } from '@/hooks/use-mobile'

/**
 * A one-shot confetti burst over the whole viewport.
 *
 * Hand-rolled on a canvas rather than pulled from a library: the whole thing is
 * ~80 lines of arithmetic, and the alternatives (canvas-confetti and friends)
 * are a dependency, a bundle, and an API surface for something the app fires
 * from exactly one place.
 *
 * Mount it to fire it — there is no imperative handle and no replay. The burst
 * belongs to the moment that rendered it, so "celebrate again" is that moment
 * mounting again, and a component that could be re-triggered would invite a
 * caller to fire it on a re-render.
 */

/** Brand-first, with enough hue spread that a handful of pieces reads as festive. */
const COLORS = [
  '#5521FF', // Electric Marine — primary
  '#B3FF3B', // Neon Lime — accent
  '#8F6BFF',
  '#FFC53D',
  '#FF5C8A',
  '#22D3EE',
]

interface Piece {
  x: number
  y: number
  vx: number
  vy: number
  gravity: number
  drag: number
  width: number
  height: number
  rotation: number
  rotationSpeed: number
  /** Phase of the flutter that squashes the piece as it turns edge-on. */
  wobble: number
  wobbleSpeed: number
  color: string
  circle: boolean
  opacity: number
}

export function Confetti() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  // Someone who has asked for less motion gets none of this: it is decoration,
  // and the surface it decorates says the same thing in words.
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  useEffect(() => {
    const canvas = canvasRef.current
    // Test DOMs have no 2D context. The celebration is decoration, so a canvas
    // that cannot be painted is simply left blank rather than throwing under a
    // component whose only job is to look nice.
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return

    let width = 0
    let height = 0
    let frame = 0
    let pieces: Piece[] = []

    /** Size the backing store to the device's pixels, then draw in CSS ones. */
    const resize = () => {
      width = window.innerWidth
      height = window.innerHeight
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      canvas.width = Math.round(width * dpr)
      canvas.height = Math.round(height * dpr)
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    const burst = (x: number, y: number, angle: number, count: number, speedScale: number) => {
      for (let i = 0; i < count; i++) {
        const speed = (8 + Math.random() * 12) * speedScale
        // Spread around the cannon's angle, so one burst is a fan rather than a line.
        const direction = angle + (Math.random() - 0.5) * 1.05
        pieces.push({
          x,
          y,
          vx: Math.cos(direction) * speed,
          vy: Math.sin(direction) * speed - Math.random() * 4,
          gravity: 0.15 + Math.random() * 0.08,
          drag: 0.989 + Math.random() * 0.007,
          width: 5 + Math.random() * 7,
          height: 8 + Math.random() * 10,
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.38,
          wobble: Math.random() * Math.PI * 2,
          wobbleSpeed: 0.08 + Math.random() * 0.12,
          color: COLORS[Math.floor(Math.random() * COLORS.length)],
          circle: Math.random() < 0.12,
          opacity: 1,
        })
      }
    }

    const draw = (piece: Piece) => {
      ctx.save()
      ctx.globalAlpha = piece.opacity
      ctx.translate(piece.x, piece.y)
      ctx.rotate(piece.rotation)
      ctx.fillStyle = piece.color

      if (piece.circle) {
        ctx.beginPath()
        ctx.arc(0, 0, piece.width / 2, 0, Math.PI * 2)
        ctx.fill()
      } else {
        // Squashing the height on a cosine is what sells these as paper: each
        // piece turns edge-on and back rather than sliding as a flat rectangle.
        const squash = Math.max(0.13, Math.abs(Math.cos(piece.wobble)))
        ctx.fillRect(-piece.width / 2, (-piece.height * squash) / 2, piece.width, piece.height * squash)
      }
      ctx.restore()
    }

    const tick = () => {
      ctx.clearRect(0, 0, width, height)

      pieces = pieces.filter((piece) => {
        piece.vx *= piece.drag
        piece.vy += piece.gravity
        piece.x += piece.vx
        piece.y += piece.vy
        piece.rotation += piece.rotationSpeed
        piece.wobble += piece.wobbleSpeed

        // Fade out near the floor so the burst ends rather than piling up.
        if (piece.y > height * 0.82) piece.opacity -= 0.014
        draw(piece)

        return piece.opacity > 0 && piece.y < height + 80 && piece.x > -100 && piece.x < width + 100
      })

      // Stop scheduling once the last piece is gone: this is a burst, not an
      // ambient effect, and a live rAF loop over an empty array would keep the
      // tab awake for as long as the surface stays open.
      if (pieces.length > 0) frame = requestAnimationFrame(tick)
      else ctx.clearRect(0, 0, width, height)
    }

    resize()
    window.addEventListener('resize', resize)

    // Two cannons from the bottom corners firing inward and up, plus a smaller
    // one over the middle of the card so there is something in front of the
    // message immediately rather than a second later.
    burst(0, height * 0.8, -Math.PI / 3.15, 115, 1.05)
    burst(width, height * 0.8, Math.PI + Math.PI / 3.15, 115, 1.05)
    burst(width * 0.5, height * 0.42, -Math.PI / 2, 70, 0.82)

    frame = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', resize)
    }
  }, [])

  if (reduceMotion) return null

  return <canvas ref={canvasRef} className={styles.canvas} aria-hidden data-testid="confetti" />
}
